import React, { useEffect, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Spinner,
    Table,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import { API } from '../../../api/API';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import Loader from '../../../Layout/Loader';

export default function CampaignOverviewContainer() {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const serverUrl = localStorage.getItem('serverUrl');
    const APP_TOKEN = localStorage.getItem('appToken');

    const [loading, setLoading] = useState<boolean>(true);
    const [campaignDetails, setCampaignDetails] = useState<any>(null);
    const [visitPlan, setVisitPlan] = useState<any[]>([]);

    // Doctor / Outlet Enrollment state
    const [activeTab, setActiveTab] = useState<'enrolled' | 'available'>('enrolled');
    const [doctorsLoading, setDoctorsLoading] = useState<boolean>(false);
    const [doctorData, setDoctorData] = useState<any[]>([]);
    const [selectedOutletIds, setSelectedOutletIds] = useState<string[]>([]);
    const [doctorSearch, setDoctorSearch] = useState<string>('');

    // Fetch Campaign Info
    const fetchCampaignInfo = async () => {
        if (!serverUrl || !APP_TOKEN || !campaignId) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.campaign_details}`;
            const { status, data } = await getData(url, true, { campiagn_id: campaignId });
            if (status === 200 && data?.data?.campiagn) {
                setCampaignDetails(data.data.campiagn);
                setVisitPlan(data.data.visit_plan || []);
            } else {
                toast.error(data?.message || 'Failed to fetch campaign details');
            }
        } catch (err) {
            console.error('Error fetching campaign details:', err);
            toast.error('Error fetching campaign details');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Doctors list (Enrolled vs Available)
    const fetchDoctorsList = async (isSelected: boolean) => {
        if (!serverUrl || !APP_TOKEN || !campaignId) return;
        setDoctorsLoading(true);
        try {
            const url = `${serverUrl}${API.doctor_list}`;
            const { status, data } = await getData(url, true, {
                campiagn_id: campaignId,
                selected: isSelected,
            });
            if (status === 200 && data?.data) {
                let items: any[] = [];
                if (isSelected) {
                    // API returns flat list or object grouped by classifications
                    if (Array.isArray(data.data)) {
                        items = data.data;
                    } else if (typeof data.data === 'object') {
                        const vals = Object.values(data.data);
                        items = vals.flatMap((v: any) => v?.Outlets || []).filter((o: any) => o?.OutletId != null || o?.OutletOrgDataId != null);
                    }
                } else {
                    if (Array.isArray(data.data)) {
                        items = data.data;
                    } else if (typeof data.data === 'object') {
                        const vals = Object.values(data.data);
                        items = vals.flatMap((v: any) => v?.Outlets || []);
                    }
                }
                setDoctorData(items);
            } else {
                setDoctorData([]);
            }
        } catch (err) {
            console.error('Error fetching doctor list:', err);
            setDoctorData([]);
        } finally {
            setDoctorsLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaignInfo();
    }, [campaignId]);

    useEffect(() => {
        fetchDoctorsList(activeTab === 'enrolled');
        setSelectedOutletIds([]);
    }, [activeTab, campaignId]);

    // Checkbox toggle handler for tagging
    const handleSelectOutlet = (idStr: string) => {
        if (selectedOutletIds.includes(idStr)) {
            setSelectedOutletIds(selectedOutletIds.filter((id) => id !== idStr));
        } else {
            setSelectedOutletIds([...selectedOutletIds, idStr]);
        }
    };

    // Select All Outlets toggle
    const handleSelectAllOutlets = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const allIds = doctorData.map((d: any, idx: number) =>
                String(d?.OutletOrgDataId || d?.OutletOrgId || d?.OutletId || idx)
            );
            setSelectedOutletIds(allIds);
        } else {
            setSelectedOutletIds([]);
        }
    };

    // Save & Enroll Tagged Outlets
    const handleTagOutlets = async () => {
        if (selectedOutletIds.length === 0) {
            toast.warning('Please select at least one outlet to enroll.');
            return;
        }
        if (!serverUrl || !APP_TOKEN) return;

        try {
            const url = `${serverUrl}${API.add_doctor}`;
            const { status, data } = await getData(url, true, {
                outlet_org_ids: selectedOutletIds.join(','),
                campiagn_id: campaignId,
                selected: true,
            });
            if (status === 200) {
                toast.success(data?.message || 'Outlets enrolled successfully!');
                setSelectedOutletIds([]);
                fetchDoctorsList(false);
            } else {
                toast.error(data?.message || 'Failed to enroll outlets');
            }
        } catch (err) {
            console.error('Error tagging outlets:', err);
            toast.error('Failed to tag outlets');
        }
    };

    // Untag single Outlet
    const handleUntagOutlet = async (item: any) => {
        if (!serverUrl || !APP_TOKEN) return;
        const outletId = String(item?.OutletOrgDataId || item?.OutletOrgId || item?.OutletId);
        try {
            const url = `${serverUrl}${API.add_doctor}`;
            const { status, data } = await getData(url, true, {
                outlet_org_ids: outletId,
                campiagn_id: campaignId,
                selected: false,
            });
            if (status === 200) {
                toast.success(data?.message || 'Outlet removed from campaign');
                fetchDoctorsList(true);
            } else {
                toast.error(data?.message || 'Failed to remove outlet');
            }
        } catch (err) {
            console.error('Error untagging outlet:', err);
            toast.error('Failed to remove outlet');
        }
    };

    // Filter doctors by search query
    const filteredDoctors = doctorData.filter((item: any) => {
        const query = doctorSearch.toLowerCase().trim();
        if (!query) return true;
        const name = (item?.OutletName || item?.OutletContactName || '').toLowerCase();
        const code = (item?.OutletOrgCode || item?.OutletCode || '').toLowerCase();
        const classification = (item?.Classification || '').toLowerCase();
        const patch = (item?.BeatName || item?.PatchName || item?.TerritoryName || '').toLowerCase();
        return name.includes(query) || code.includes(query) || classification.includes(query) || patch.includes(query);
    });

    if (loading) {
        return (
            <div className="page-body p-5">
                <Loader height="300px" />
            </div>
        );
    }

    return (
        <div className="page-body p-2">
            <Container fluid className="px-2">
                {/* Navigation Back Header */}
                <div className="d-flex align-items-center mb-3">
                    <Button
                        color="light"
                        size="sm"
                        className="me-3 d-flex align-items-center border"
                        onClick={() => navigate('/brandCampaign')}
                    >
                        <FeatherIconCom iconName="ArrowLeft" size={16} className="me-1" />
                        Back to Campaigns
                    </Button>
                    <h5 className="mb-0 fw-bold">Campaign Details</h5>
                </div>

                {/* Hero Banner Card */}
                <Card className="border-0 bg-primary text-white shadow-sm mb-4">
                    <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-2">
                            <Badge color="light" className="text-primary fw-bold text-uppercase px-3 py-1 me-2">
                                <FeatherIconCom iconName="Radio" size={12} className="me-1" />
                                {campaignDetails?.CampianStatus || 'CAMPAIGN'} • ID #{campaignDetails?.BrandCampiagnId || campaignId}
                            </Badge>
                        </div>
                        <h3 className="fw-bold mb-1">{campaignDetails?.CampiagnName || 'Campaign Details'}</h3>
                        <p className="mb-0 opacity-75">
                            <FeatherIconCom iconName="Calendar" size={14} className="me-1" />
                            {campaignDetails?.StartDate ? moment(campaignDetails.StartDate).format('DD MMM YYYY') : '-'}
                            {'  —  '}
                            {campaignDetails?.EndDate ? moment(campaignDetails.EndDate).format('DD MMM YYYY') : '-'}
                        </p>
                    </CardBody>
                </Card>

                {/* Metrics Summary Grid */}
                <Row className="g-3 mb-4">
                    <Col md={3} sm={6}>
                        <Card className="border shadow-none mb-0 text-center">
                            <CardBody className="p-3">
                                <div className="text-muted small mb-1">Start Date</div>
                                <div className="fw-bold text-dark">
                                    {campaignDetails?.StartDate ? moment(campaignDetails.StartDate).format('DD MMM YYYY') : '-'}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="border shadow-none mb-0 text-center">
                            <CardBody className="p-3">
                                <div className="text-muted small mb-1">End Date</div>
                                <div className="fw-bold text-dark">
                                    {campaignDetails?.EndDate ? moment(campaignDetails.EndDate).format('DD MMM YYYY') : '-'}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="border shadow-none mb-0 text-center">
                            <CardBody className="p-3">
                                <div className="text-muted small mb-1">Campaign Type</div>
                                <div className="fw-bold text-dark">{campaignDetails?.CampiagnType || '-'}</div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="border shadow-none mb-0 text-center">
                            <CardBody className="p-3">
                                <div className="text-muted small mb-1">Status</div>
                                <div className="fw-bold text-success">{campaignDetails?.CampianStatus || '-'}</div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Supporting Material, Poster, Objective & Coverage */}
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <Card className="border shadow-none h-100">
                            <CardHeader className="bg-light py-2">
                                <h6 className="mb-0 text-primary fw-bold">Supporting Attachment & Media</h6>
                            </CardHeader>
                            <CardBody>
                                <div className="mb-3">
                                    <Label className="fw-semibold text-muted small d-block">Attachment Material:</Label>
                                    {campaignDetails?.Material ? (
                                        <a
                                            href={campaignDetails.Material}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                                        >
                                            <FeatherIconCom iconName="FileText" size={14} className="me-1" />
                                            View Material File
                                            <FeatherIconCom iconName="ExternalLink" size={12} className="ms-2" />
                                        </a>
                                    ) : (
                                        <span className="text-muted fst-italic">No Attachment Available</span>
                                    )}
                                </div>
                                <div>
                                    <Label className="fw-semibold text-muted small d-block">Poster / Media:</Label>
                                    {campaignDetails?.Media ? (
                                        <a
                                            href={campaignDetails.Media}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-info d-inline-flex align-items-center"
                                        >
                                            <FeatherIconCom iconName="Image" size={14} className="me-1" />
                                            View Poster Media
                                            <FeatherIconCom iconName="ExternalLink" size={12} className="ms-2" />
                                        </a>
                                    ) : (
                                        <span className="text-muted fst-italic">No Poster Media Available</span>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="border shadow-none h-100">
                            <CardHeader className="bg-light py-2">
                                <h6 className="mb-0 text-primary fw-bold">Objective & Coverage</h6>
                            </CardHeader>
                            <CardBody>
                                <div className="mb-3">
                                    <Label className="fw-semibold text-muted small d-block">Objective:</Label>
                                    <div className="text-dark">
                                        {campaignDetails?.Description || 'No detailed objective provided.'}
                                    </div>
                                </div>
                                <div>
                                    <Label className="fw-semibold text-muted small d-block">Classifications Coverage:</Label>
                                    <Badge color="light" className="text-dark border px-2 py-1">
                                        {campaignDetails?.Classifications || 'All Classifications'}
                                    </Badge>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Visit Plan Table */}
                <Card className="border shadow-none mb-4">
                    <CardHeader className="bg-light py-2 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 text-primary fw-bold">Visit Plan</h6>
                    </CardHeader>
                    <CardBody className="p-0">
                        {visitPlan && visitPlan.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Visit Level</th>
                                            <th>Description</th>
                                            <th>Agenda Type</th>
                                            <th>Input / SGPI Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visitPlan.map((vp: any, idx: number) => (
                                            <tr key={idx}>
                                                <td className="fw-semibold">Visit {vp?.StepLevel || idx + 1}</td>
                                                <td>{vp?.Description || '-'}</td>
                                                <td>
                                                    <Badge color="info" pill>{vp?.AgendaType || 'FW'}</Badge>
                                                </td>
                                                <td>{vp?.SgpiId || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="p-4 text-center text-muted fst-italic">
                                No visit plan details configured for this campaign.
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Doctor / Outlet Enrollment Section */}
                <Card className="border shadow-none mb-4">
                    <CardHeader className="bg-light py-2">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h6 className="mb-0 text-primary fw-bold">
                                    <FeatherIconCom iconName="Users" size={16} className="me-1" />
                                    Outlet / Doctor Enrollment
                                </h6>
                                <small className="text-muted">Manage mapped outlets for campaign participation</small>
                            </div>

                            {/* Tab Switcher */}
                            <Nav pills className="bg-white p-1 border rounded">
                                <NavItem>
                                    <NavLink
                                        className={`py-1 px-3 style-pointer ${activeTab === 'enrolled' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('enrolled')}
                                    >
                                        Currently Enrolled
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={`py-1 px-3 style-pointer ${activeTab === 'available' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('available')}
                                    >
                                        Available to Enroll
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </CardHeader>
                    <CardBody>
                        {/* Search & Actions Bar */}
                        <div className="row g-2 mb-3 align-items-center">
                            <div className="col-md-6 col-sm-12">
                                <Input
                                    type="text"
                                    placeholder="Search by outlet name, code, specialty or patch..."
                                    value={doctorSearch}
                                    onChange={(e) => setDoctorSearch(e.target.value)}
                                    className="form-control"
                                />
                            </div>

                            {activeTab === 'available' && (
                                <div className="col-md-6 col-sm-12 d-flex justify-content-end align-items-center gap-2">
                                    <span className="text-muted small">
                                        {selectedOutletIds.length} selected
                                    </span>
                                    <Button
                                        color="success"
                                        size="sm"
                                        disabled={selectedOutletIds.length === 0}
                                        onClick={handleTagOutlets}
                                    >
                                        <FeatherIconCom iconName="UserPlus" size={14} className="me-1" />
                                        Save & Enroll Selected
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Doctors Table */}
                        <div className="table-responsive border rounded">
                            {doctorsLoading ? (
                                <Loader height="160px" />
                            ) : filteredDoctors.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    No outlets found for this view.
                                </div>
                            ) : (
                                <Table hover className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            {activeTab === 'available' && (
                                                <th style={{ width: '40px' }}>
                                                    <Input
                                                        type="checkbox"
                                                        onChange={handleSelectAllOutlets}
                                                        checked={
                                                            filteredDoctors.length > 0 &&
                                                            selectedOutletIds.length === filteredDoctors.length
                                                        }
                                                    />
                                                </th>
                                            )}
                                            <th>Outlet Name</th>
                                            <th>Code</th>
                                            <th>Specialty / Classification</th>
                                            <th>Patch / Beat</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDoctors.map((item: any, idx: number) => {
                                            const idStr = String(item?.OutletOrgDataId || item?.OutletOrgId || item?.OutletId || idx);
                                            const isSelected = selectedOutletIds.includes(idStr);

                                            return (
                                                <tr key={idStr}>
                                                    {activeTab === 'available' && (
                                                        <td>
                                                            <Input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectOutlet(idStr)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="fw-semibold text-dark">
                                                        {item?.OutletSalutation || ''} {item?.OutletName || item?.OutletContactName || '-'}
                                                    </td>
                                                    <td><code>{item?.OutletOrgCode || item?.OutletCode || '-'}</code></td>
                                                    <td>
                                                        <Badge color="light" className="text-dark border">
                                                            {item?.Classification || 'General'}
                                                        </Badge>
                                                    </td>
                                                    <td>{item?.BeatName || item?.PatchName || item?.TerritoryName || '-'}</td>
                                                    <td className="text-end">
                                                        {activeTab === 'enrolled' ? (
                                                            <Button
                                                                color="danger"
                                                                outline
                                                                size="sm"
                                                                className="py-0 px-2"
                                                                onClick={() => handleUntagOutlet(item)}
                                                            >
                                                                <FeatherIconCom iconName="Trash2" size={14} className="me-1" />
                                                                Remove
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                color={isSelected ? 'success' : 'primary'}
                                                                outline={!isSelected}
                                                                size="sm"
                                                                className="py-0 px-2"
                                                                onClick={() => handleSelectOutlet(idStr)}
                                                            >
                                                                {isSelected ? 'Selected' : 'Select'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
}
